import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const BulkUploadModal = ({ onClose, onUpload }) => {
  const [uploadMethod, setUploadMethod] = useState('excel');
  const [selectedFile, setSelectedFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewData, setPreviewData] = useState([]);
  const [errors, setErrors] = useState([]);
  const [duplicatePassports, setDuplicatePassports] = useState([]);

  // Mock existing passport numbers in the system
  const existingPassports = ['A1234567', 'C5555555', 'G3333333'];

  const mockExcelData = [
    {
      name: 'John Doe',
      passport: 'A1234567',
      nationality: 'India',
      skill: 'Construction Worker',
      phone: '+91 9876543210',
      email: 'john.doe@email.com',
      status: 'Error - Duplicate passport (already exists in system)'
    },
    {
      name: 'Jane Smith',
      passport: 'B9876543',
      nationality: 'Bangladesh',
      skill: 'Domestic Helper',
      phone: '+880 1234567890',
      email: 'jane.smith@email.com',
      status: 'Valid'
    },
    {
      name: 'Kumar Thapa',
      passport: 'X9999999',
      nationality: 'Nepal',
      skill: 'Driver',
      phone: '+977 9876543210',
      email: 'kumar@email.com',
      status: 'Valid'
    },
    {
      name: 'Invalid Entry',
      passport: '',
      nationality: 'Nepal',
      skill: 'Driver',
      phone: '+977 9876543210',
      email: 'invalid@email.com',
      status: 'Error - Missing passport'
    },
    {
      name: 'Duplicate in Batch',
      passport: 'B9876543',
      nationality: 'Bangladesh',
      skill: 'Cook',
      phone: '+880 1234567891',
      email: 'duplicate@email.com',
      status: 'Error - Duplicate passport within this batch'
    }
  ];

  const validatePassports = (data) => {
    const duplicates = [];
    const errors = [];
    const seenPassports = new Set();
    
    data.forEach((item, index) => {
      if (!item.passport) {
        data[index].status = 'Error - Missing passport';
        errors.push(`Row ${index + 1}: Missing passport number`);
        return;
      }
      
      // Check against existing system passports
      if (existingPassports.includes(item.passport)) {
        data[index].status = 'Error - Duplicate passport (already exists in system)';
        duplicates.push(item.passport);
        errors.push(`Row ${index + 1}: Passport ${item.passport} already exists in system`);
        return;
      }
      
      // Check for duplicates within the current batch
      if (seenPassports.has(item.passport)) {
        data[index].status = 'Error - Duplicate passport within this batch';
        errors.push(`Row ${index + 1}: Passport ${item.passport} appears multiple times in this upload`);
        return;
      }
      
      seenPassports.add(item.passport);
      data[index].status = 'Valid';
    });
    
    setDuplicatePassports(duplicates);
    setErrors(errors);
    return data;
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      
      // Mock file processing with passport validation
      setTimeout(() => {
        const validatedData = validatePassports([...mockExcelData]);
        setPreviewData(validatedData);
      }, 1000);
    }
  };

  const handleOCRUpload = (event) => {
    const files = Array.from(event.target.files);
    setSelectedFile({ name: `${files.length} documents selected`, files });
    
    // Mock OCR processing
    setTimeout(() => {
      setPreviewData([
        {
          name: 'Extracted from Document 1',
          passport: 'C1111111',
          nationality: 'Philippines',
          skill: 'Domestic Helper',
          phone: '+63 9123456789',
          email: 'extracted1@email.com',
          status: 'OCR Extracted'
        },
        {
          name: 'Extracted from Document 2',
          passport: 'D2222222',
          nationality: 'Indonesia',
          skill: 'Factory Worker',
          phone: '+62 8123456789',
          email: 'extracted2@email.com',
          status: 'OCR Extracted'
        }
      ]);
    }, 2000);
  };

  const handleUpload = () => {
    if (!selectedFile) return;
    
    setIsProcessing(true);
    setUploadProgress(0);
    
    // Mock upload progress
    const interval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setIsProcessing(false);
          onUpload({
            method: uploadMethod,
            file: selectedFile,
            data: previewData.filter(item => !item.status.includes('Error'))
          });
          return 100;
        }
        return prev + 10;
      });
    }, 200);
  };

  const downloadTemplate = () => {
    // Mock template download
    const csvContent = "data:text/csv;charset=utf-8," +"Name,Passport,Nationality,Skill,Phone,Email\n" +"John Doe,A1234567,India,Construction Worker,+91 9876543210,john.doe@email.com\n" +"Jane Smith,B9876543,Bangladesh,Domestic Helper,+880 1234567890,jane.smith@email.com";
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "biodata_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="fixed inset-0 bg-secondary-900 bg-opacity-50 flex items-center justify-center z-1001 p-4">
      <div className="bg-surface rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-secondary-200">
          <div>
            <h2 className="text-xl font-bold text-text-primary">Bulk Upload Biodata</h2>
            <p className="text-text-secondary">Upload multiple biodata entries at once</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-secondary-600 hover:bg-secondary-100 rounded-md transition-colors duration-150"
          >
            <Icon name="X" size={20} />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[70vh]">
          {/* Upload Method Selection */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-text-primary mb-4">Choose Upload Method</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div
                onClick={() => setUploadMethod('excel')}
                className={`p-4 border-2 rounded-lg cursor-pointer transition-all duration-150 ${
                  uploadMethod === 'excel' ?'border-primary bg-primary-50' :'border-secondary-200 hover:border-secondary-300'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    uploadMethod === 'excel' ? 'bg-primary text-white' : 'bg-secondary-100 text-secondary-600'
                  }`}>
                    <Icon name="FileSpreadsheet" size={20} />
                  </div>
                  <div>
                    <h4 className="font-medium text-text-primary">Excel Upload</h4>
                    <p className="text-sm text-text-secondary">Upload biodata from Excel/CSV file</p>
                  </div>
                </div>
              </div>
              
              <div
                onClick={() => setUploadMethod('ocr')}
                className={`p-4 border-2 rounded-lg cursor-pointer transition-all duration-150 ${
                  uploadMethod === 'ocr' ?'border-primary bg-primary-50' :'border-secondary-200 hover:border-secondary-300'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    uploadMethod === 'ocr' ? 'bg-primary text-white' : 'bg-secondary-100 text-secondary-600'
                  }`}>
                    <Icon name="ScanLine" size={20} />
                  </div>
                  <div>
                    <h4 className="font-medium text-text-primary">OCR Upload</h4>
                    <p className="text-sm text-text-secondary">Extract data from document images</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Upload Area */}
          <div className="mb-6">
            {uploadMethod === 'excel' ? (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-text-primary">Excel/CSV Upload</h3>
                  <button
                    onClick={downloadTemplate}
                    className="btn-secondary px-4 py-2 rounded-lg font-medium flex items-center"
                  >
                    <Icon name="Download" size={16} className="mr-2" />
                    Download Template
                  </button>
                </div>
                
                <div className="border-2 border-dashed border-secondary-300 rounded-lg p-8 text-center hover:border-primary transition-colors duration-150">
                  <Icon name="Upload" size={48} className="text-secondary-400 mx-auto mb-4" />
                  <h4 className="text-lg font-medium text-text-primary mb-2">Upload Excel File</h4>
                  <p className="text-text-secondary mb-4">
                    Drag and drop your Excel/CSV file here, or click to browse
                  </p>
                  <input
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileSelect}
                    className="hidden"
                    id="excel-upload"
                  />
                  <label
                    htmlFor="excel-upload"
                    className="btn-primary px-6 py-3 rounded-lg font-medium cursor-pointer"
                  >
                    Choose File
                  </label>
                  {selectedFile && (
                    <p className="text-sm text-success mt-3">
                      Selected: {selectedFile.name}
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div>
                <h3 className="text-lg font-medium text-text-primary mb-4">OCR Document Upload</h3>
                <div className="border-2 border-dashed border-secondary-300 rounded-lg p-8 text-center hover:border-primary transition-colors duration-150">
                  <Icon name="Camera" size={48} className="text-secondary-400 mx-auto mb-4" />
                  <h4 className="text-lg font-medium text-text-primary mb-2">Upload Documents</h4>
                  <p className="text-text-secondary mb-4">
                    Upload passport copies, forms, or other documents for OCR processing
                  </p>
                  <input
                    type="file"
                    multiple
                    accept="image/*,.pdf"
                    onChange={handleOCRUpload}
                    className="hidden"
                    id="ocr-upload"
                  />
                  <label
                    htmlFor="ocr-upload"
                    className="btn-primary px-6 py-3 rounded-lg font-medium cursor-pointer"
                  >
                    Choose Documents
                  </label>
                  {selectedFile && (
                    <p className="text-sm text-success mt-3">
                      {selectedFile.name}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
          
          {/* Preview Data */}
          {previewData.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-text-primary mb-4">Preview Data</h3>
              
              {errors.length > 0 && (
                <div className="bg-error-50 border border-error-200 rounded-lg p-4 mb-4">
                  <div className="flex items-start space-x-3">
                    <Icon name="AlertTriangle" size={20} className="text-error-600 mt-0.5" />
                    <div className="flex-1">
                      <h5 className="font-medium text-error-800">Validation Errors Found</h5>
                      <ul className="text-sm text-error-700 mt-1 space-y-1">
                        {errors.map((error, index) => (
                          <li key={index}>• {error}</li>
                        ))}
                      </ul>
                      
                      {duplicatePassports.length > 0 && (
                        <div className="mt-4 p-3 bg-error-100 rounded border border-error-300">
                          <h6 className="font-medium text-error-800 mb-2">
                            Duplicate Passport Numbers Detected:
                          </h6>
                          <div className="flex flex-wrap gap-2">
                            {duplicatePassports.map((passport, index) => (
                              <span 
                                key={index}
                                className="inline-flex items-center px-2 py-1 bg-error-200 text-error-800 text-xs font-mono rounded"
                              >
                                {passport}
                              </span>
                            ))}
                          </div>
                          <p className="text-xs text-error-700 mt-2">
                            These passport numbers already exist in the system and cannot be uploaded again.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-secondary-200">
                  <thead className="bg-secondary-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase">Passport</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase">Nationality</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase">Skill</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-secondary-200">
                    {previewData.map((item, index) => (
                      <tr key={index} className={item.status.includes('Error') ? 'bg-error-50' : ''}>
                        <td className="px-4 py-3 text-sm">{item.name}</td>
                        <td className="px-4 py-3 text-sm font-mono">{item.passport}</td>
                        <td className="px-4 py-3 text-sm">{item.nationality}</td>
                        <td className="px-4 py-3 text-sm">{item.skill}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            item.status.includes('Error') 
                              ? 'bg-error-100 text-error-800' : item.status.includes('OCR')
                              ? 'bg-accent-100 text-accent-800' :'bg-success-100 text-success-800'
                          }`}>
                            {item.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          {/* Upload Progress */}
          {isProcessing && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-text-primary">Processing...</span>
                <span className="text-sm text-text-secondary">{uploadProgress}%</span>
              </div>
              <div className="w-full bg-secondary-200 rounded-full h-2">
                <div 
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-secondary-200">
          <div className="text-sm text-text-secondary">
            {previewData.length > 0 && (
              <span>
                {previewData.filter(item => !item.status.includes('Error')).length} valid entries, 
                {previewData.filter(item => item.status.includes('Error')).length} errors
              </span>
            )}
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="btn-secondary px-4 py-2 rounded-lg font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleUpload}
              disabled={!selectedFile || isProcessing || previewData.filter(item => !item.status.includes('Error')).length === 0}
              className="btn-primary px-6 py-2 rounded-lg font-medium flex items-center disabled:opacity-50"
            >
              {isProcessing ? (
                <>
                  <Icon name="Loader2" size={16} className="mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Icon name="Upload" size={16} className="mr-2" />
                  Upload Biodata
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkUploadModal;